import React, { useState, useMemo } from 'react';
import { Button } from '@/components';
import { FilterPopup, FilterGroup } from '@/components';

interface UniversalFilterConfig {
  key: string;
  title: string;
  icon: string;
  multiple?: boolean;
  dataSource?: any[];
  staticOptions?: Array<{
    key: string;
    label: string;
    value: string | boolean;
  }>;
  labelField?: string; // Field to use for label (default: 'name')
  labelFormatter?: (item: any) => string; // Custom function to format label
  valueField?: string; // Field to use for value (default: 'ID' or 'id')
}

interface UniversalFilterProps {
  filters: UniversalFilterConfig[];
  currentFilters?: Record<string, any>;
  onApply: (filters: Record<string, any>) => void;
  onClear: () => void;
  className?: string;
  buttonText?: string;
  buttonIcon?: string;
  /**
   * Badge number, when the caller counts differently from "every selected
   * value". A page whose defaults are pre-selected needs this: those are not
   * filters the user applied.
   */
  count?: number;
}

const UniversalFilter: React.FC<UniversalFilterProps> = ({
  filters,
  currentFilters = {},
  onApply,
  onClear,
  className = '',
  buttonText = 'Filter',
  buttonIcon = 'ri-filter-3-fill',
  count,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Convert the filter configs into FilterGroup format
  const filterGroups: FilterGroup[] = useMemo(() => {
    return filters.map((filter) => {
      let options: Array<{
        key: string;
        label: string;
        value: string | boolean;
      }> = [];

      if (filter.staticOptions) {
        // Use static options for predefined values (like status, partnership, etc.)
        options = filter.staticOptions;
      } else if (filter.dataSource && filter.dataSource.length > 0) {
        // Use dynamic data source for entities (OEMs, models, clients, etc.)
        const valueField = filter.valueField || 'ID';

        options = filter.dataSource.map((item) => {
          let label: string;

          if (filter.labelFormatter) {
            // Use custom label formatter function
            label = filter.labelFormatter(item);
          } else {
            // Use simple field name (backward compatibility)
            const labelField = filter.labelField || 'name';
            label = item[labelField] || item.name || 'Unknown';
          }

          return {
            key: `${filter.key}_${item[valueField] || item.id}`,
            label,
            value: String(item[valueField] || item.id),
          };
        });
      }

      return {
        title: filter.title,
        key: filter.key,
        multiple: filter.multiple ?? true,
        icon: filter.icon,
        options,
      };
    });
  }, [filters]);

  const handleApply = (appliedFilters: Record<string, any>) => {
    onApply(appliedFilters);
    setIsFilterOpen(false);
  };

  const handleClear = () => {
    onClear();
    setIsFilterOpen(false);
  };

  const handleClose = () => {
    setIsFilterOpen(false);
  };

  // Calculate number of selected filters
  const derivedFiltersCount = useMemo(() => {
    let count = 0;
    Object.keys(currentFilters).forEach((key) => {
      const filterValue = currentFilters[key];
      if (Array.isArray(filterValue)) {
        count += filterValue.length;
      } else if (
        filterValue !== undefined &&
        filterValue !== null &&
        filterValue !== ''
      ) {
        count += 1;
      }
    });
    return count;
  }, [currentFilters]);

  const selectedFiltersCount = count ?? derivedFiltersCount;

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="neutral"
        className="flex !border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50 border-[1px]"
        size="small"
        onClick={() => setIsFilterOpen(true)}
      >
        <i
          className={`${buttonIcon} text-[12px] ${
            selectedFiltersCount > 0 ? 'text-[#5e2aac]' : ''
          } mr-1`}
        ></i>
        {buttonText}
        {selectedFiltersCount > 0 && (
          <span className="ml-1 bg-[#5e2aac] text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[16px] h-4 flex items-center justify-center">
            {selectedFiltersCount}
          </span>
        )}
      </Button>{' '}
      {/* Filter Popup */}
      {isFilterOpen && (
        <FilterPopup
          isOpen={isFilterOpen}
          onClose={handleClose}
          onApply={handleApply}
          onClear={handleClear}
          currentFilters={currentFilters}
          filterGroups={filterGroups}
        />
      )}
    </div>
  );
};

export default UniversalFilter;
